import * as React from 'react';
import clsx from 'clsx';
import { createStyles, makeStyles } from '@material-ui/core/styles';

interface LoaderProps {
    label?: string;
    size?: 'large' | 'medium' | 'small' | 'big' | 'huge' | 'massive';
}

// RvLoader used to wrap Semantic UI's <Loader active inline="centered">. It is drawn here with
// the same rules Semantic UI 2.2.12's loader.css applies to `.ui.{size}.active.centered.inline.loader`
// (plus `.text` when a label is given), so every existing <RvLoader /> looks and lays out exactly
// as before without depending on Semantic UI or its global stylesheet.
//
// Semantic sizes the ring in rem against its own 14px root font-size (html { font-size: 14px }),
// so the geometry below is the same values in px - it no longer moves if that root size changes
// when Semantic's stylesheet is removed. The ring's border width and the label's font-size stay
// in em (relative to the surrounding text), exactly as Semantic had them, so a loader still
// scales with the container it is dropped into.
interface SizeMetrics {
    // Ring diameter (also the label loader's min-width).
    diameter: number;
    // Space kept above the label for the ring, only used when a label is shown.
    textPaddingTop: number;
    fontSize: string;
}

const SIZE_METRICS: { [size: string]: SizeMetrics } = {
    small: { diameter: 24, textPaddingTop: 35, fontSize: '.92857143em' },
    // Semantic has no `medium` loader rules - it falls through to the default size.
    medium: { diameter: 32, textPaddingTop: 43, fontSize: '1em' },
    large: { diameter: 48, textPaddingTop: 59, fontSize: '1.14285714em' },
    big: { diameter: 52, textPaddingTop: 63, fontSize: '1.28571429em' },
    huge: { diameter: 58, textPaddingTop: 69, fontSize: '1.42857143em' },
    massive: { diameter: 64, textPaddingTop: 75, fontSize: '1.71428571em' },
};

const sizeRules = ({ diameter, textPaddingTop, fontSize }: SizeMetrics) => ({
    width: diameter,
    height: diameter,
    fontSize: fontSize,
    '&::before, &::after': {
        width: diameter,
        height: diameter,
        margin: `0 0 0 ${-diameter / 2}px`,
    },
    '&$text': {
        minWidth: diameter,
        paddingTop: textPaddingTop,
    },
});

const useStyles = makeStyles(() =>
    createStyles({
        '@keyframes rvLoaderSpin': {
            from: { transform: 'rotate(0)' },
            to: { transform: 'rotate(360deg)' },
        },
        loader: {
            display: 'block',
            position: 'relative',
            top: 0,
            left: 0,
            margin: '0 auto',
            verticalAlign: 'middle',
            textAlign: 'center',
            zIndex: 1000,
            transform: 'none',
            boxSizing: 'border-box',
            // Faint full ring behind the moving arc.
            '&::before, &::after': {
                position: 'absolute',
                content: '""',
                top: 0,
                left: '50%',
                boxSizing: 'border-box',
                borderRadius: '500rem',
            },
            '&::before': {
                border: '.2em solid rgba(0,0,0,.1)',
            },
            '&::after': {
                animation: '$rvLoaderSpin .6s linear infinite',
                borderColor: '#767676 transparent transparent',
                borderStyle: 'solid',
                borderWidth: '.2em',
                boxShadow: '0 0 0 1px transparent',
            },
        },
        small: sizeRules(SIZE_METRICS.small),
        medium: sizeRules(SIZE_METRICS.medium),
        large: sizeRules(SIZE_METRICS.large),
        big: sizeRules(SIZE_METRICS.big),
        huge: sizeRules(SIZE_METRICS.huge),
        massive: sizeRules(SIZE_METRICS.massive),
        // Label variant: the loader grows to fit its text, with the ring above it.
        text: {
            width: 'auto !important',
            height: 'auto !important',
            textAlign: 'center',
            fontStyle: 'normal',
        },
    })
);

const RvLoaderIndicator: React.FC<{ label?: string; size: string }> = ({ label, size }) => {
    const classes = useStyles();

    return (
        <div className={clsx(classes.loader, (classes as { [name: string]: string })[size], !!label && classes.text)}>
            {label}
        </div>
    );
};

export class RvLoader extends React.Component<LoaderProps, {}> {

    public render() {

        return (
            <div>
                <RvLoaderIndicator
                    size={this.props.size ? this.props.size : 'small'}
                    label={this.props.label}
                />
            </div>
        );
    }
}
